import { PermissionGroup } from './types';
import {
  ACTION_DISPLAY_MAP,
  ACTION_PRIORITY,
  getResourceAndScope,
  getAction,
  createPermissionCode,
  getScopeDisplayName,
  formatResourceName,
  getActionDisplayText,
} from '../../../../utils';

/**
 * Shared permission helpers live in `RolesAdmin/utils` so the read-only
 * RoleDisplay and this editable PermissionTable stay in sync. Re-exported here
 * so existing edit-modal imports keep working unchanged.
 */
export {
  ACTION_DISPLAY_MAP,
  ACTION_PRIORITY,
  getResourceAndScope,
  getAction,
  createPermissionCode,
  getScopeDisplayName,
  formatResourceName,
  getActionDisplayText,
};

/**
 * Determine the category of a permission group for sorting
 */
export const getPermissionGroupCategory = (group: PermissionGroup): number => {
  // Check what types of actions this group has
  const actions = new Set<string>();

  group.permissions.forEach((permission) => {
    const action = getAction(permission.code);
    actions.add(action);
  });

  // Category 1: Groups with read/write/delete capabilities
  if (actions.has('write') || actions.has('delete')) {
    return 1;
  }

  // Category 3: Groups related to exports
  if (
    actions.has('export') &&
    !actions.has('read') &&
    !actions.has('write') &&
    !actions.has('delete') &&
    !actions.has('show')
  ) {
    return 3;
  }

  // Category 2: Groups with only view/show capabilities
  if (actions.has('read') || actions.has('show')) {
    return 2;
  }

  // Default category (should rarely happen)
  return 4;
};

/**
 * Create a test ID from a prefix and name
 */
export const createTestId = (prefix: string, name: string): string =>
  `${prefix}-${name.toLowerCase().replace(/\s+/g, '-')}`;

/**
 * Check if a resource is a special resource
 */
export const isSpecialResource = (resource: string): boolean =>
  resource === 'internal_banner' ||
  resource === 'reimbursement' ||
  resource === 'map';

/**
 * Check if a permission group has special resources
 */
export const hasSpecialResources = (group: PermissionGroup): boolean =>
  group.permissions.some((permission) => {
    const { resource } = getResourceAndScope(permission.code);
    return isSpecialResource(resource);
  });
